import { useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useConfig } from '@revenuewell/logic-config';
import { PrimaryButton, SecondaryButton } from 'apps/wrapper/src/components/shared/buttons/buttons.styles';
import { useEventBus } from 'apps/wrapper/src/hooks/use-event-bus';
import AppointmentService from 'apps/wrapper/src/services/appointment/appointment-service';
import { AppointmentRequestStatus } from 'apps/wrapper/src/services/appointment/types/appointment-request-status';
import { FC, useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useApptRequestsWidget } from '../../hooks/use-appt-requests-widget';
import { useRequestDetailsView } from '../../hooks/use-request-details-view';
import { AiTranscriptSection } from '../../shared/ai-transcript/ai-transcript-section';
import { InsuranceInfoSection } from '../../shared/insurance-info-section';
import { NotesSection } from '../../shared/notes-section/notes-section';
import { PmsStatusSection } from '../../shared/pms-status/pms-status-section';
import { Page, PageContent, PageTitle, Toolbar } from '../styles';
import { RequestDetailsViewSkeleton } from './request-details.skeleton';
import { PatientDetailsSection } from './sections/patient-details/patient-details-section';
import { PreferredDateSection } from './sections/preferred-date/preferred-date-section';
import { RequestDateSection } from './sections/request-date/request-date-section';
import { MapCalendarRequest } from './utils/calendar-request-mapper';

export const RequestDetailsView: FC = () => {
  const { config } = useConfig();
  const { oidcService, getClaims } = useOidcWithAdmin();
  const { showError, showSuccess } = useNotification();
  const { publishToIframes } = useEventBus();
  const { locationId, goToCalendarApp, goBack, markRequestAsRead } = useApptRequestsWidget();
  const { isLoading, requestDetails, isEditable } = useRequestDetailsView();
  const [isRequestingSchedule, setIsRequestingSchedule] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);

  useEffect(() => {
    if (!requestDetails) return;

    publishToIframes({ messageType: 'appt-request-reset' });
    markRequestAsRead(requestDetails.id);
  }, [requestDetails]);

  const handleScheduleClick = async () => {
    if (!requestDetails) return;

    setIsRequestingSchedule(true);

    await goToCalendarApp();

    setTimeout(() => {
      publishToIframes({
        messageType: 'appt-request-schedule',
        data: MapCalendarRequest(requestDetails, requestDetails.appointmentRequestTimes[0])
      });
    }, 200);

    setTimeout(() => setIsRequestingSchedule(false), 2000);
  };

  const handleDismissRequestClick = async () => {
    try {
      setIsDismissing(true);
      const claims = await getClaims();

      await appointmentService.dismissAppointmentRequest(requestDetails.id, locationId, claims.masterAccountId);
      publishToIframes({ messageType: 'appt-request-reset' });
      publishToIframes({ messageType: 'appt-request-refresh-calendar' });
      showSuccess('Appointment request dismissed');
      goBack(true);
    } catch {
      showError('Unable to dismiss appointment request');
    } finally {
      setIsDismissing(false);
    }
  };

  if (isLoading) return <RequestDetailsViewSkeleton />;

  return (
    <Page>
      <PageTitle>Appointment Details</PageTitle>
      <PageContent sx={{ margin: '0' }}>
        <Scrollbars autoHide>
          <PatientDetailsSection />
          <InsuranceInfoSection insuranceInfo={requestDetails.insuranceInfo} />
          <RequestDateSection />
          <PreferredDateSection />
          <AiTranscriptSection
            isLoading={isLoading}
            caseId={requestDetails.milaCaseId}
            patientId={requestDetails.patient?.id}
          />
          {requestDetails.isDirectlyBooked || requestDetails.status === AppointmentRequestStatus.Confirmed ? (
            <PmsStatusSection
              isLoading={isLoading}
              pmsWritebackStatus={requestDetails.pmsWritebackStatus}
              requestId={requestDetails.id}
            />
          ) : null}
          <NotesSection locationId={locationId} requestDetails={requestDetails} />
        </Scrollbars>
        {!requestDetails?.isDirectlyBooked && (
          <Toolbar sx={{ flexDirection: 'column', borderTop: 'none' }}>
            <PrimaryButton
              loading={isRequestingSchedule}
              disabled={isLoading || !isEditable}
              onClick={handleScheduleClick}
              sx={{ marginBottom: '15px' }}
              disableElevation
            >
              Add To Schedule
            </PrimaryButton>
            <SecondaryButton
              loading={isDismissing}
              disabled={isLoading || !isEditable}
              onClick={handleDismissRequestClick}
              disableElevation
            >
              Dismiss Request
            </SecondaryButton>
          </Toolbar>
        )}
      </PageContent>
    </Page>
  );
};
